import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';

import { createSignatureNodeView } from './SignatureNodeView';

export const SIGNATURE_NODE_NAME = 'signature';

/**
 * A numeric attribute off an HTML round-trip, or null.
 *
 * Everything comes back as a string, and `Number('')` is 0 — which for a width means a stamp
 * with no size at all rather than "the author never set one". Absent, empty and unparseable all
 * collapse to null so the node view falls back to the design size.
 */
const numberAttribute = (element: HTMLElement, name: string): number | null => {
	const raw = element.getAttribute(name);
	if (raw === null || raw.trim() === '') {
		return null;
	}
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
};

/** What the host knows about a slot that the document itself does not carry. */
export interface SignatureSlotDisplay {
	label: string;
	roleType: DocumentSigningRoleType;
}

export interface SignatureNodeOptions {
	/**
	 * The slot's label and role, keyed by `signatureSlotKey`.
	 *
	 * Held here for the same reason `FieldNode` holds `labelFor`: the document stores the slot
	 * TUPLE, and "Investor #1 — Witness" is derived from the template's signing roles, which
	 * change independently of the document. Storing the label would freeze a stale one into a
	 * deed. Absent = draw the tuple, which only happens for a role since deleted.
	 */
	displayFor?: (slotKey: string) => SignatureSlotDisplay | undefined;
	/** Whether stamps render their metadata block, which sets the node's aspect ratio. */
	showStampMetadata?: boolean;
}

/**
 * Where a signature stamp goes, as a block node in the document.
 *
 * `atom: true` for the same reason `FieldNode` is one: the slot tuple is data, not spelling, so
 * the caret steps over it and one backspace removes the whole anchor. `group: 'block'` makes it
 * legal wherever a paragraph is — including a table cell, which is where a Word signature block
 * actually lives.
 *
 * It occupies real vertical space on purpose. The alternative — a zero-height marker — would let
 * the author place a stamp on top of the clause below it, because nothing in the layout would
 * know to move that clause out of the way.
 */
export const SignatureNode = Node.create<SignatureNodeOptions>({
	name: SIGNATURE_NODE_NAME,
	group: 'block',
	atom: true,
	selectable: true,
	draggable: true,

	addOptions() {
		return {};
	},

	addAttributes() {
		return {
			signingRoleId: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-signing-role-id'),
				renderHTML: (attributes) => ({
					'data-signing-role-id': attributes.signingRoleId,
				}),
			},
			isWitness: {
				default: false,
				// Round-trips through an HTML attribute, so it comes back as a STRING. Parsed
				// here rather than trusted: `'false'` is truthy, and a witness stamp drawn for
				// the primary signer is a wrong name on an executed deed.
				parseHTML: (element) => element.getAttribute('data-is-witness') === 'true',
				renderHTML: (attributes) => ({
					'data-is-witness': attributes.isWitness ? 'true' : 'false',
				}),
			},
			signatureIndex: {
				default: 0,
				parseHTML: (element) => Number(element.getAttribute('data-signature-index') ?? 0),
				renderHTML: (attributes) => ({
					'data-signature-index': String(attributes.signatureIndex),
				}),
			},
			/**
			 * The author's own size and horizontal position, both absent until they touch one.
			 *
			 * `null` rather than a resolved default, because the DEFAULT is not a constant: it
			 * depends on `showStampMetadata` and on whether this is a witness, neither of which
			 * the document carries. Baking today's answer in at insert time would freeze it
			 * against a template whose metadata setting later changes.
			 *
			 * Height is not here for the same reason it is not in the flow schema: it follows
			 * the width through the anchor's own ratio, and a second copy is one that can lose.
			 */
			widthMm: {
				default: null,
				parseHTML: (element) => numberAttribute(element, 'data-width-mm'),
				renderHTML: (attributes) =>
					attributes.widthMm === null || attributes.widthMm === undefined
						? {}
						: { 'data-width-mm': String(attributes.widthMm) },
			},
			offsetXMm: {
				default: null,
				parseHTML: (element) => numberAttribute(element, 'data-offset-x-mm'),
				renderHTML: (attributes) =>
					attributes.offsetXMm === null || attributes.offsetXMm === undefined
						? {}
						: { 'data-offset-x-mm': String(attributes.offsetXMm) },
			},
		};
	},

	parseHTML() {
		return [{ tag: 'div[data-signature-slot]' }];
	},

	/** The fallback markup — copy-paste and any non-node-view render path. */
	renderHTML({ HTMLAttributes }) {
		return [
			'div',
			mergeAttributes(HTMLAttributes, {
				'data-signature-slot': '',
				class: 'ds-signature',
			}),
		];
	},

	renderText() {
		return '';
	},

	/**
	 * `this.options` is typed here — `SignatureNodeOptions`, from `Node.create<…>` — so the
	 * view is built with them rather than reading them back off the extension.
	 *
	 * The alternative was `extension.options as SignatureNodeOptions` inside the view, and
	 * `NodeViewProps.extension` is `Node<any, any>`: that assertion type-checks against
	 * anything, so renaming an option here would have left the view compiling while every
	 * anchor silently drew "Unassigned signature". This way a rename is a compile error.
	 */
	addNodeView() {
		return ReactNodeViewRenderer(createSignatureNodeView(this.options));
	},
});

/** The slot key for a signature node's attributes, narrowed from ProseMirror's `Attrs`. */
export const signatureNodeSlotKey = (attrs: Record<string, unknown>): string =>
	signatureSlotKey({
		signingRoleId: String(attrs.signingRoleId ?? ''),
		isWitness: attrs.isWitness === true,
		signatureIndex: Number(attrs.signatureIndex ?? 0),
	});
